const isMediaSourceSupported = () => {
  return 'MediaSource' in window && MediaSource.isTypeSupported('audio/mpeg');
};

/*
This class is used to play streaming (chunked) audio received from 
Websocket using the "modern" MediaSource Extensions APIs.

Note: This is not compatible with iOS devices. 
*/
class MediaSourceAudioPlayer {
  private mediaSource!: MediaSource;
  private audioBufferQueue: ArrayBuffer[] = [];
  private sourceBuffer: SourceBuffer | null = null;
  private audio!: HTMLAudioElement;

  init() {
    this.mediaSource = new MediaSource();
    this.audio = new Audio();
    this.setupMediaSource();
  }

  private addSourceBuffer = () => {
    this.sourceBuffer = this.mediaSource.addSourceBuffer('audio/mpeg');
    this.sourceBuffer.addEventListener('updateend', this.playNextChunk);
  };

  private playNextChunk = () => {
    // if there are more audio chunks in the queue, append the next one if the source buffer is not updating
    if (this.audioBufferQueue.length > 0) {
      if (this.sourceBuffer && !this.sourceBuffer.updating) {
        this.sourceBuffer.appendBuffer(this.audioBufferQueue[0]);
        this.audioBufferQueue.shift();
      }
    }
  };

  private setupMediaSource() {
    this.mediaSource.addEventListener('sourceopen', this.addSourceBuffer);

    this.audio.src = URL.createObjectURL(this.mediaSource);
    this.audio.play();
  }

  private cleanup() {
    // Clean up event listeners
    if (this.sourceBuffer && this.mediaSource) {
      this.sourceBuffer.removeEventListener('updateend', this.playNextChunk);
      if (this.mediaSource.sourceBuffers.length > 0) {
        this.mediaSource.removeSourceBuffer(this.sourceBuffer);
      }
      this.mediaSource.removeEventListener('sourceopen', this.addSourceBuffer);
    }

    // Clean up audio element
    if (this.audio) {
      this.audio.pause();
      if (this.audio.src) {
        URL.revokeObjectURL(this.audio.src);
      }
    }

    // Clear the queue
    this.audioBufferQueue = [];

    // Close the MediaSource if it's open
    if (this.mediaSource.readyState === 'open') {
      this.mediaSource.endOfStream();
    }
  }

  play(audio_chunk?: ArrayBuffer) {
    if (audio_chunk) {
      this.audioBufferQueue.push(audio_chunk);
    }

    this.playNextChunk();
  }

  pause() {
    this.audio.pause();
  }

  reset() {
    this.cleanup();
    this.init();
  }

  close() {
    this.cleanup();
  }
}

class WebAudioPlayer {
  private context!: AudioContext;
  // @ts-ignore
  private sourceBuffer!: AudioBufferSourceNode;

  init() {
    // Create a dedicated AudioContext for TTS with different sample rate to avoid conflicts with STT
    this.context = new (window.AudioContext ||
      (window as any).WebkitAudioContext)({
      sampleRate: 44100, // Use 44.1kHz for TTS (standard audio playback)
      latencyHint: 'playback',
    });
    this.sourceBuffer = this.context.createBufferSource();
    this.sourceBuffer.connect(this.context.destination);
    this.sourceBuffer.start(0);

    if ((window as any).isMicOn) {
      // hack to play audio on iOS
      navigator.mediaDevices.getUserMedia({
        audio: true,
      });
    }
  }

  play(audio: ArrayBuffer) {
    this.sourceBuffer = this.context.createBufferSource();
    this.sourceBuffer.connect(this.context.destination);
    this.sourceBuffer.start(0);

    this.context.decodeAudioData(audio, (buffer: AudioBuffer) => {
      this.sourceBuffer.buffer = buffer;
    });
  }

  pause() {
    this.sourceBuffer?.stop();
  }

  reset() {
    this.context?.close();
    this.sourceBuffer?.disconnect();
    this.init();
  }

  close() {
    this.context?.suspend();
  }
}

class AudioPlayer {
  private player: MediaSourceAudioPlayer | WebAudioPlayer;

  constructor() {
    this.player = isMediaSourceSupported()
      ? new MediaSourceAudioPlayer()
      : new WebAudioPlayer();
  }

  init() {
    this.player.init();
  }

  play(audio: ArrayBuffer) {
    this.player.play(audio);
  }

  pause() {
    this.player.pause();
  }

  reset() {
    this.player.reset();
  }

  close() {
    this.player.close();
  }
}

const audioPlayer = new AudioPlayer();

export { audioPlayer };
